import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrivacyLinks from "@/components/PrivacyLinks";
import FileUploadZone from "@/components/FileUploadZone";
import DichiarazioniBlock from "@/components/DichiarazioniBlock";
import MupConsegnaModal from "@/components/MupConsegnaModal";
import { IddVitaStep, AmlVerificaStep } from "@/components/wizard-vita/IddAmlSteps";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, ArrowRight, Lock, Send, Loader2 } from "lucide-react";
import { renderAutoStep } from "@/components/questionnaire/AutoSteps";
import { renderCasaStep } from "@/components/questionnaire/CasaSteps";
import { renderVitaStep } from "@/components/questionnaire/VitaSteps";
import { renderAziendeStep } from "@/components/questionnaire/AziendeSteps";
import { renderInfortuniStep } from "@/components/questionnaire/InfortuniSteps";
import { renderViaggiStep } from "@/components/questionnaire/ViaggiSteps";
import AccountOptInCheckbox from "@/components/wizard/AccountOptInCheckbox";
import AccountCredentialsBox from "@/components/wizard/AccountCredentialsBox";
import { trackWizardSubmit, trackAccountCreated } from "@/lib/analytics";
import usePartnerTracking from "@/hooks/usePartnerTracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONFIGS = {
  auto: {
    title: "Assicurazione Auto",
    subtitle: "Proteggi il tuo veicolo con una polizza su misura",
    steps: ["Dati Veicolo", "Dati Assicurato", "Coperture", "Documenti", "Contatto"],
    documents: [
      { key: "libretto_fronte", label: "Libretto di circolazione - FRONTE", required: true },
      { key: "libretto_retro", label: "Libretto di circolazione - RETRO", required: true },
      { key: "documento_identita_fronte", label: "Documento d'identità - FRONTE", required: true },
      { key: "documento_identita_retro", label: "Documento d'identità - RETRO", required: true },
    ],
  },
  casa: {
    title: "Assicurazione Casa",
    subtitle: "Proteggi la tua abitazione e il suo contenuto",
    // v1.74.0 — Rimossi step "Documenti" + relativi files (lead-only, uniforme cross-wizard).
    steps: ["Dati Immobile", "Valutazione Immobile", "Valore e Rischi", "Contatto"],
    documents: [],
  },
  vita: {
    title: "Assicurazione Vita",
    subtitle: "Investi nel tuo futuro e proteggi chi ami",
    // FASE 4.2.11.10 — +2 step finali compliance IDD/AML (Reg. IVASS 41/2018 + 44/2019)
    steps: ["Dati Personali", "Obiettivi", "Documenti", "Profilatura IDD", "Verifica AML", "Contatto"],
    documents: [
      { key: "documento_identita_fronte", label: "Documento d'identità - FRONTE", required: true },
      { key: "documento_identita_retro", label: "Documento d'identità - RETRO", required: true },
    ],
  },
  aziende: {
    title: "Assicurazione Aziende e Professionisti",
    subtitle: "Soluzioni dedicate per la tua attività professionale",
    steps: ["Dati Azienda", "Fatturato e Rischi", "Sede operativa", "Documenti", "Contatto"],
    documents: [
      { key: "visura_camerale", label: "Visura camerale", required: true },
      { key: "doc_amministratore_fronte", label: "Documento amministratore - FRONTE", required: true },
      { key: "doc_amministratore_retro", label: "Documento amministratore - RETRO", required: true },
    ],
  },
  infortuni: {
    title: "Assicurazione Infortuni",
    subtitle: "Protezione per te e la tua famiglia in caso di infortunio",
    steps: ["Attività e Rischi", "Massimali e Familiari", "Documenti", "Contatto"],
    documents: [
      { key: "documento_identita_fronte", label: "Documento d'identità - FRONTE", required: true },
      { key: "documento_identita_retro", label: "Documento d'identità - RETRO", required: true },
      { key: "badge_aziendale", label: "Badge aziendale (per convenzioni)", required: false },
      { key: "ultima_busta_paga", label: "Ultima busta paga — per valutare la rateizzazione in 10 rate senza interessi", required: true },
    ],
  },
  viaggi: {
    title: "Assicurazione Viaggi",
    subtitle: "Protezione completa per i tuoi viaggi in Italia e all'estero",
    steps: ["Destinazione e Durata", "Viaggiatori e Coperture", "Documenti", "Contatto"],
    documents: [
      { key: "documento_identita_fronte", label: "Documento d'identità - FRONTE", required: true },
      { key: "documento_identita_retro", label: "Documento d'identità - RETRO", required: true },
      { key: "badge_aziendale", label: "Badge aziendale (per convenzioni)", required: false },
      { key: "ultima_busta_paga", label: "Ultima busta paga — per valutare la rateizzazione in 10 rate senza interessi", required: true },
    ],
  },
};

export default function QuestionnairePage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const config = CONFIGS[type];

  // ITER 69 — Hook DRY per lettura partner/sede/azienda dai query params
  // (impostati da ConvenzioniPage al momento del navigate).
  const { partnerId, metadata: partnerMetadata } = usePartnerTracking();

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = avanti, -1 = indietro
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // FASE K2 LEGACY — response post-submit con credenziali one-time
  const [postSubmitData, setPostSubmitData] = useState(null);

  // FASE 4.2.11.9 — MupConsegnaModal pre-submit gate (IVASS Reg. 40/2018)
  const [mupModalOpen, setMupModalOpen] = useState(false);
  const [mupConfirmed, setMupConfirmed] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  if (!config) { navigate("/"); return null; }

  const totalSteps = config.steps.length;
  const progress = ((step + 1) / totalSteps) * 100;

  // Supporta sia update("key", val) sia update({k1: v1, k2: v2}) — FASE 4.2.11.10
  const update = (keyOrPatch, val) => {
    if (typeof keyOrPatch === "object" && keyOrPatch !== null) {
      setFormData(prev => ({ ...prev, ...keyOrPatch }));
    } else {
      setFormData(prev => ({ ...prev, [keyOrPatch]: val }));
    }
  };
  const toggleArray = (key, val) => {
    setFormData(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] };
    });
  };
  const handleFileUpload = (docKey, fileData) => {
    setFiles(prev => { const n = { ...prev }; if (fileData) n[docKey] = fileData; else delete n[docKey]; return n; });
  };

  const handleSubmit = async (mupBypass = false) => {
    if (!formData.nome || !formData.cognome || !formData.email || !formData.telefono) {
      toast.error("Compila tutti i campi obbligatori del contatto"); return;
    }
    if (!formData.privacy) { toast.error("Devi accettare la privacy policy per procedere"); return; }

    // FASE 4.2.11.9 — gate IVASS pre-submit (Reg. 40/2018 post Provv. 147/2024)
    // FASE 4.2.11.11 fix: mupBypass esplicito per evitare closure stale.
    if (!mupBypass && !mupConfirmed) {
      setMupModalOpen(true);
      return;
    }

    setSubmitting(true);
    try {
      // FASE K2 LEGACY — passa crea_account_ok top-level per opt-in
      const { data: resp } = await axios.post(`${API}/submissions`, {
        type,
        data: formData,
        files: Object.values(files),
        contact: {
          nome: formData.nome, cognome: formData.cognome,
          email: formData.email, telefono: formData.telefono,
          codice_fiscale: formData.codice_fiscale || formData.cf || null,
        },
        crea_account_ok: !!formData.crea_account_ok,
        // FIX BUG "Convenzione persa" — propaga contesto convenzione al backend.
        // Il backend salva questi campi in `metadata.utm_source` (compat schema)
        // + `partner` top-level per filtro cross-ramo in /admin/richieste.
        partner_id: partnerId,
        metadata: partnerMetadata,
      });
      // Analytics tracking
      try {
        trackWizardSubmit({ ramo: type });
        if (resp?.account_cliente?.created) trackAccountCreated({ ramo: type });
      } catch { /* ignore */ }
      // Se opt-in con account creato → mostra schermata credenziali one-time
      // in-page (NON redirect a /conferma per NON perdere temp_password).
      if (resp?.account_cliente?.created) {
        setPostSubmitData(resp);
      } else {
        navigate("/conferma");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Errore nell'invio della richiesta. Riprova."); }
    finally { setSubmitting(false); }
  };

  const canGoNext = () => {
    if (step === totalSteps - 1) return formData.nome && formData.cognome && formData.email && formData.telefono && formData.privacy;
    return true;
  };

  const renderStepContent = () => {
    const currentStepName = config.steps[step];
    // FASE 4.2.11.10 — step compliance vita IDD/AML
    if (type === "vita" && currentStepName === "Profilatura IDD") {
      return <IddVitaStep formData={formData} update={update} richiestaId={null} />;
    }
    if (type === "vita" && currentStepName === "Verifica AML") {
      return <AmlVerificaStep formData={formData} update={update} richiestaId={null} />;
    }
    if (currentStepName === "Documenti") return <DocsStep config={config} type={type} formData={formData} handleFileUpload={handleFileUpload} />;
    if (currentStepName === "Contatto") return <ContactStep type={type} formData={formData} update={update} />;
    if (type === "auto") return renderAutoStep(step, formData, update, toggleArray);
    if (type === "casa") return renderCasaStep(step, formData, update, toggleArray);
    if (type === "vita") return renderVitaStep(step, formData, update, toggleArray);
    if (type === "aziende") return renderAziendeStep(step, formData, update, toggleArray);
    if (type === "infortuni") return renderInfortuniStep(step, formData, update, toggleArray);
    if (type === "viaggi") return renderViaggiStep(step, formData, update, toggleArray);
    return null;
  };

  // FASE K2 LEGACY — Schermata post-submit con credenziali account cliente
  if (postSubmitData) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="questionnaire-post-submit">
          <div className="max-w-2xl mx-auto px-6 py-12 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <Send className="w-8 h-8 text-emerald-700" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-[#0F1F14] mb-2">
              Richiesta inviata!
            </h1>
            <p className="text-sm text-[#4A5E51] mb-6">
              Un consulente Colucci Assicuratori ti ricontatterà entro 24 ore.
              {postSubmitData.id && (
                <> Riferimento pratica: <strong>{postSubmitData.id.substring(0, 8).toUpperCase()}</strong></>
              )}
            </p>
            <AccountCredentialsBox account={postSubmitData.account_cliente} />
            <div className="mt-8">
              <Button
                onClick={() => navigate("/")}
                className="min-h-[48px] px-8 rounded-full bg-[#163321] text-white hover:bg-[#0e1f15]"
                data-testid="questionnaire-post-home"
              >
                Torna alla home
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="questionnaire-page">
        <div className="max-w-2xl mx-auto px-6 py-12">
          <div className="text-center mb-8">
            <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-[#0F1F14] mb-2" data-testid="questionnaire-title">{config.title}</h1>
            <p className="text-sm text-[#4A5E51]">{config.subtitle}</p>
          </div>
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-[#4A5E51]">Passo {step + 1} di {totalSteps}: {config.steps[step]}</span>
              <motion.span
                key={Math.round(progress)}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-xs text-[#4A5E51] tabular-nums">
                {Math.round(progress)}%
              </motion.span>
            </div>
            <div className="h-1.5 bg-[#E5E2DB] rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#163321] to-[#D97B58] rounded-full"
                initial={false}
                animate={{ width: `${progress}%` }}
                transition={{ type: "spring", stiffness: 180, damping: 25 }}
                data-testid="progress-bar" />
            </div>
            {/* Dots indicator */}
            <div className="flex items-center justify-center gap-1.5 mt-3">
              {config.steps.map((_, i) => (
                <motion.div
                  key={i}
                  animate={{
                    scale: i === step ? 1.3 : 1,
                    backgroundColor: i < step ? "#163321" : i === step ? "#D97B58" : "#E5E2DB",
                  }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="w-1.5 h-1.5 rounded-full" />
              ))}
            </div>
          </div>
          <div className="relative overflow-hidden">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={step}
                custom={direction}
                initial={{ x: direction * 40, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -direction * 40, opacity: 0 }}
                transition={{ duration: 0.28, ease: [0.25, 0.1, 0.25, 1] }}
                className="bg-white rounded-2xl border border-[#E5E2DB] p-6 sm:p-8 mb-6 shadow-sm"
                data-testid={`step-content-${step}`}>
                <h2 className="font-heading text-lg font-medium text-[#0F1F14] mb-5">{config.steps[step]}</h2>
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="flex items-center gap-2 justify-center mb-6 text-xs text-[#4A5E51]">
            <Lock className="w-3 h-3" /><span>I tuoi dati sono al sicuro e protetti</span>
          </div>
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={() => { setDirection(-1); setStep(s => s - 1); }} disabled={step === 0}
              className="rounded-full px-6 h-11 border-[#E5E2DB] text-[#163321] hover:bg-[#F5F4F0] text-sm font-medium disabled:opacity-30" data-testid="prev-step-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
            </Button>
            {step < totalSteps - 1 ? (
              <Button onClick={() => { setDirection(1); setStep(s => s + 1); }} className="rounded-full px-6 h-11 bg-[#163321] text-white hover:bg-[#224A32] text-sm font-medium" data-testid="next-step-btn">
                Avanti <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting || !canGoNext()}
                className="rounded-full px-6 h-11 bg-[#D97B58] text-white hover:bg-[#C06A48] text-sm font-medium disabled:opacity-50" data-testid="submit-questionnaire-btn">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio in corso...</> : <><Send className="w-4 h-4 mr-2" /> Invia Richiesta</>}
              </Button>
            )}
          </div>
          <div className="mt-8 text-center">
            <p className="text-xs text-[#4A5E51] italic" data-testid="human-touch-reminder">"Parlerai con un consulente reale, non con un sistema automatico"</p>
          </div>
          <PrivacyLinks className="mt-4" />
        </div>
      </main>
      <Footer />

      {/* FASE 4.2.11.9 — MupConsegnaModal gating IVASS prima del submit finale */}
      <MupConsegnaModal
        open={mupModalOpen}
        onClose={() => setMupModalOpen(false)}
        onConfirm={() => {
          setMupConfirmed(true);
          setMupModalOpen(false);
          // FASE 4.2.11.11 fix: bypass esplicito, no setTimeout/state stale.
          handleSubmit(true);
        }}
        prodotto={type}
        richiestaId={null}
        customerId={null}
      />
    </>
  );
}

/* --- Documenti Step --- */
function DocsStep({ config, type, formData, handleFileUpload }) {
  let docs = config.documents;
  if (type === "auto" && formData.tipo_assicurato === "azienda") {
    docs = [
      { key: "libretto_fronte", label: "Libretto di circolazione - FRONTE", required: true },
      { key: "libretto_retro", label: "Libretto di circolazione - RETRO", required: true },
      { key: "visura_camerale", label: "Visura camerale", required: true },
      { key: "documento_identita_fronte", label: "Carta d'identità amministratore - FRONTE", required: true },
      { key: "documento_identita_retro", label: "Carta d'identità amministratore - RETRO", required: true },
    ];
  }
  return (
    <div className="space-y-6">
      <p className="text-sm text-[#4A5E51] mb-2">Carica i documenti necessari. I tuoi dati sono al sicuro.</p>
      {docs.map(doc => (
        <FileUploadZone key={doc.key} label={doc.label} required={doc.required} hint="Ci serve per offrirti la soluzione migliore" onFileUploaded={(data) => handleFileUpload(doc.key, data)} />
      ))}
    </div>
  );
}

/* --- Contatto Step --- */
function ContactStep({ type, formData, update }) {
  const showCoincide = type === "auto";  const isAzienda = formData.tipo_assicurato === "azienda";
  const hasData = isAzienda ? formData.ragione_sociale : formData.nome_conducente && formData.cognome_conducente;
  const risposta = formData.dati_coincidono;

  return (
    <div className="space-y-5">
      {showCoincide && (
        <div className="p-5 bg-[#F5F4F0] rounded-2xl border border-[#E5E2DB] space-y-4">
          <p className="text-sm font-medium text-[#0F1F14]">I dati dell'assicurato coincidono con quelli del contraente?</p>
          <div className="flex gap-3">
            <button type="button" onClick={() => {
                update("dati_coincidono", "si");
                if (hasData) { if (isAzienda) { update("nome", formData.ragione_sociale); update("cognome", ""); } else { update("nome", formData.nome_conducente); update("cognome", formData.cognome_conducente); } }
              }}
              className={`flex-1 py-3 px-6 rounded-xl text-sm font-medium transition-all border-2 ${risposta === "si" ? "bg-[#163321] text-white border-[#163321]" : "bg-white text-[#0F1F14] border-[#E5E2DB] hover:border-[#163321]/40"}`}
              data-testid="coincidono-si-btn">Si, coincidono</button>
            <button type="button" onClick={() => { update("dati_coincidono", "no"); update("nome", ""); update("cognome", ""); }}
              className={`flex-1 py-3 px-6 rounded-xl text-sm font-medium transition-all border-2 ${risposta === "no" ? "bg-[#163321] text-white border-[#163321]" : "bg-white text-[#0F1F14] border-[#E5E2DB] hover:border-[#163321]/40"}`}
              data-testid="coincidono-no-btn">No, sono diversi</button>
          </div>
          {risposta === "si" && hasData && (
            <p className="text-sm text-[#4A5E51]">Contraente: <span className="font-medium text-[#0F1F14]">{isAzienda ? formData.ragione_sociale : `${formData.nome} ${formData.cognome}`}</span></p>
          )}
        </div>
      )}
      {(risposta !== "si" || !hasData) && (!showCoincide || risposta === "no" || (risposta === "si" && !hasData)) && (
        <>
          <p className="text-sm text-[#4A5E51]">Inserisci i dati del contraente. Un nostro consulente ti ricontatterà entro 24 ore.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm text-[#0F1F14]">Nome <span className="text-[#D97B58]">*</span></Label>
              <Input value={formData.nome || ""} onChange={e => update("nome", e.target.value)} className="rounded-xl border-[#E5E2DB] h-11 focus:ring-2 focus:ring-[#163321]/20 focus:border-[#163321]" placeholder="Il tuo nome" data-testid="contact-nome" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-[#0F1F14]">Cognome <span className="text-[#D97B58]">*</span></Label>
              <Input value={formData.cognome || ""} onChange={e => update("cognome", e.target.value)} className="rounded-xl border-[#E5E2DB] h-11 focus:ring-2 focus:ring-[#163321]/20 focus:border-[#163321]" placeholder="Il tuo cognome" data-testid="contact-cognome" />
            </div>
          </div>
        </>
      )}
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Email <span className="text-[#D97B58]">*</span></Label>
        <Input type="email" value={formData.email || ""} onChange={e => update("email", e.target.value)} className="rounded-xl border-[#E5E2DB] h-11 focus:ring-2 focus:ring-[#163321]/20 focus:border-[#163321]" placeholder="la.tua@email.it" data-testid="contact-email" />
      </div>
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Telefono <span className="text-[#D97B58]">*</span></Label>
        <Input type="tel" value={formData.telefono || ""} onChange={e => update("telefono", e.target.value)} className="rounded-xl border-[#E5E2DB] h-11 focus:ring-2 focus:ring-[#163321]/20 focus:border-[#163321]" placeholder="+39 333 1234567" data-testid="contact-telefono" />
      </div>
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Note aggiuntive</Label>
        <Textarea value={formData.note || ""} onChange={e => update("note", e.target.value)} className="rounded-xl border-[#E5E2DB] focus:ring-2 focus:ring-[#163321]/20 focus:border-[#163321] min-h-[80px]" placeholder="Informazioni aggiuntive per il consulente..." data-testid="contact-note" />
      </div>
      <DichiarazioniBlock formData={formData} update={update} />
      {/* FASE K2 LEGACY — opt-in creazione account cliente (default deselezionato, GDPR opt-in esplicito) */}
      <AccountOptInCheckbox
        value={!!formData.crea_account_ok}
        onChange={(v) => update("crea_account_ok", v)}
      />
    </div>
  );
}
